import React, { useState } from 'react';

import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Button, 
  Form, 
  Table, 
  Badge, 
  Modal, 
  Nav, 
  ProgressBar, 
  InputGroup 
} from 'react-bootstrap';

// Aday veri tipi tanımı
interface Lead {
  id: number;
  name: string;
  company: string;
  email: string;
  phone: string;
  status: string;
  source: string;
  lastContact: string;
  score: number;
}

// Kampanya veri tipi tanımı
interface Campaign {
  id: number;
  name: string;
  status: string;
  leads: number;
  conversion: number;
  startDate: string;
  endDate: string;
}

// Örnek aday verileri
const dummyLeads: Lead[] = [
  {
    id: 1,
    name: 'John Smith',
    company: 'Tech Solutions Inc.',
    email: 'john.smith@techsolutions.com',
    phone: '+1 234-567-8901',
    status: 'New',
    source: 'Website',
    lastContact: '2024-03-15',
    score: 85,
  },
  {
    id: 2,
    name: 'Sarah Johnson',
    company: 'Global Industries',
    email: 'sarah.j@globalind.com',
    phone: '+1 234-567-8902',
    status: 'Contacted',
    source: 'LinkedIn',
    lastContact: '2024-03-14',
    score: 92,
  },
  {
    id: 3,
    name: 'Michael Brown',
    company: 'Innovation Labs',
    email: 'm.brown@innovationlabs.com',
    phone: '+1 234-567-8903',
    status: 'Qualified',
    source: 'Email Campaign',
    lastContact: '2024-03-13',
    score: 78,
  },
];

// Örnek kampanya verileri
const dummyCampaigns: Campaign[] = [
  {
    id: 1,
    name: 'Q1 Tech Industry Outreach',
    status: 'Active',
    leads: 150,
    conversion: 35,
    startDate: '2024-01-01',
    endDate: '2024-03-31',
  },
  {
    id: 2,
    name: 'LinkedIn Connection Drive',
    status: 'Active',
    leads: 75,
    conversion: 42,
    startDate: '2024-02-15',
    endDate: '2024-04-15',
  },
];

// İstatistik verileri
const statistics = {
  totalLeads: 1250,
  activeCampaigns: 5,
  conversionRate: 28.5,
  qualifiedLeads: 350,
};

const LeadGenerationPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('leads');
  const [showNewLeadModal, setShowNewLeadModal] = useState<boolean>(false);
  const [showNewCampaignModal, setShowNewCampaignModal] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Durum rengini belirleyen yardımcı fonksiyon
  const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'new':
        return 'info';
      case 'contacted':
        return 'warning';
      case 'qualified':
        return 'success';
      default:
        return 'secondary';
    }
  };

  // Arama sonuçlarına göre filtrelenmiş adaylar
  const filteredLeads = dummyLeads.filter(lead => 
    lead.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    lead.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
    lead.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Container fluid className="py-4">
      {/* Başlık Bölümü */}
      <Row className="mb-4 align-items-center">
        <Col>
          <h2 className="fw-bold mb-0">Lead Generation</h2>
        </Col>
        <Col xs="auto" className="d-flex gap-2">
          <Button 
            variant="primary" 
            className="d-flex align-items-center"
            onClick={() => setShowNewCampaignModal(true)}
            style={{
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              border: 'none',
              borderRadius: '0.5rem',
            }}
          >
            <i className="bi bi-plus-lg me-2"></i>
            New Campaign
          </Button>
          <Button 
            variant="outline-primary" 
            className="d-flex align-items-center"
            onClick={() => setShowNewLeadModal(true)}
            style={{ borderRadius: '0.5rem' }}
          >
            <i className="bi bi-plus-lg me-2"></i>
            Add Lead
          </Button>
        </Col>
      </Row>

      {/* İstatistik Kartları */}
      <Row className="g-3 mb-4">
        <Col xs={12} sm={6} md={3}>
          <Card className="h-100 border-0 shadow-sm rounded-3" style={{ transition: 'transform 0.2s' }}>
            <Card.Body>
              <div className="d-flex align-items-center mb-3">
                <div className="d-flex align-items-center justify-content-center rounded-circle bg-primary bg-opacity-10 me-3" style={{ width: '48px', height: '48px' }}>
                  <i className="bi bi-people-fill fs-4 text-primary"></i>
                </div>
                <h6 className="text-muted mb-0">Total Leads</h6>
              </div>
              <h3 className="fw-bold mb-2">{statistics.totalLeads}</h3>
              <ProgressBar now={75} variant="primary" style={{ height: '6px', borderRadius: '3px' }} />
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} md={3}>
          <Card className="h-100 border-0 shadow-sm rounded-3" style={{ transition: 'transform 0.2s' }}>
            <Card.Body>
              <div className="d-flex align-items-center mb-3">
                <div className="d-flex align-items-center justify-content-center rounded-circle bg-success bg-opacity-10 me-3" style={{ width: '48px', height: '48px' }}>
                  <i className="bi bi-megaphone-fill fs-4 text-success"></i>
                </div>
                <h6 className="text-muted mb-0">Active Campaigns</h6>
              </div>
              <h3 className="fw-bold mb-2">{statistics.activeCampaigns}</h3>
              <ProgressBar now={60} variant="success" style={{ height: '6px', borderRadius: '3px' }} />
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} md={3}>
          <Card className="h-100 border-0 shadow-sm rounded-3" style={{ transition: 'transform 0.2s' }}>
            <Card.Body>
              <div className="d-flex align-items-center mb-3">
                <div className="d-flex align-items-center justify-content-center rounded-circle bg-warning bg-opacity-10 me-3" style={{ width: '48px', height: '48px' }}>
                  <i className="bi bi-graph-up-arrow fs-4 text-warning"></i>
                </div>
                <h6 className="text-muted mb-0">Conversion Rate</h6>
              </div>
              <h3 className="fw-bold mb-2">{statistics.conversionRate}%</h3>
              <ProgressBar now={statistics.conversionRate} variant="warning" style={{ height: '6px', borderRadius: '3px' }} />
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} md={3}>
          <Card className="h-100 border-0 shadow-sm rounded-3" style={{ transition: 'transform 0.2s' }}>
            <Card.Body>
              <div className="d-flex align-items-center mb-3">
                <div className="d-flex align-items-center justify-content-center rounded-circle bg-info bg-opacity-10 me-3" style={{ width: '48px', height: '48px' }}>
                  <i className="bi bi-building fs-4 text-info"></i>
                </div>
                <h6 className="text-muted mb-0">Qualified Leads</h6>
              </div>
              <h3 className="fw-bold mb-2">{statistics.qualifiedLeads}</h3>
              <ProgressBar now={45} variant="info" style={{ height: '6px', borderRadius: '3px' }} />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Sekmeler */}
      <Nav variant="tabs" className="mb-4">
        <Nav.Item>
          <Nav.Link 
            active={activeTab === 'leads'} 
            onClick={() => setActiveTab('leads')}
          >
            Leads
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            active={activeTab === 'campaigns'} 
            onClick={() => setActiveTab('campaigns')}
          >
            Campaigns
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            active={activeTab === 'analytics'} 
            onClick={() => setActiveTab('analytics')}
          >
            Analytics
          </Nav.Link>
        </Nav.Item>
      </Nav>

      {/* Adaylar Tablosu */}
      {activeTab === 'leads' && (
        <Card className="border-0 shadow-sm rounded-3">
          <Card.Body>
            <div className="d-flex justify-content-between mb-4">
              <InputGroup style={{ maxWidth: '300px' }}>
                <InputGroup.Text className="bg-transparent border-end-0">
                  <i className="bi bi-search"></i>
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search leads..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="border-start-0"
                />
              </InputGroup>
              <Button variant="outline-secondary" className="d-flex align-items-center">
                <i className="bi bi-funnel me-2"></i>
                Filter
              </Button>
            </div>
            
            <div className="table-responsive">
              <Table hover>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Company</th>
                    <th>Contact</th>
                    <th>Status</th>
                    <th>Source</th>
                    <th>Last Contact</th>
                    <th>Score</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.map(lead => (
                    <tr key={lead.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '36px', height: '36px' }}>
                            {lead.name.charAt(0)}
                          </div>
                          <span className="fw-medium">{lead.name}</span>
                        </div>
                      </td>
                      <td>{lead.company}</td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button variant="primary" size="sm" className="rounded-circle p-1">
                            <i className="bi bi-envelope-fill"></i>
                          </Button>
                          <Button variant="primary" size="sm" className="rounded-circle p-1">
                            <i className="bi bi-telephone-fill"></i>
                          </Button>
                        </div>
                      </td>
                      <td>
                        <Badge bg={getStatusColor(lead.status)} pill>
                          {lead.status}
                        </Badge>
                      </td>
                      <td>{lead.source}</td>
                      <td>{lead.lastContact}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          <span className="me-2">{lead.score}</span>
                          <ProgressBar 
                            now={lead.score} 
                            style={{ width: '50px', height: '6px', borderRadius: '3px' }} 
                          />
                        </div>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button variant="primary" size="sm" className="rounded-circle p-1">
                            <i className="bi bi-pencil-fill"></i>
                          </Button>
                          <Button variant="danger" size="sm" className="rounded-circle p-1">
                            <i className="bi bi-trash-fill"></i>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Kampanyalar Tablosu */}
      {activeTab === 'campaigns' && (
        <Card className="border-0 shadow-sm rounded-3">
          <Card.Body>
            <div className="table-responsive">
              <Table hover>
                <thead>
                  <tr>
                    <th>Campaign Name</th>
                    <th>Status</th>
                    <th>Leads</th>
                    <th>Conversion</th>
                    <th>Duration</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {dummyCampaigns.map(campaign => (
                    <tr key={campaign.id}>
                      <td className="fw-medium">{campaign.name}</td>
                      <td>
                        <Badge bg="success" pill>
                          {campaign.status}
                        </Badge>
                      </td>
                      <td>{campaign.leads}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          <span className="me-2">{campaign.conversion}%</span>
                          <ProgressBar 
                            now={campaign.conversion} 
                            style={{ width: '50px', height: '6px', borderRadius: '3px' }} 
                          />
                        </div>
                      </td>
                      <td>
                        {campaign.startDate} - {campaign.endDate}
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button variant="primary" size="sm" className="rounded-circle p-1">
                            <i className="bi bi-pencil-fill"></i>
                          </Button>
                          <Button variant="danger" size="sm" className="rounded-circle p-1">
                            <i className="bi bi-trash-fill"></i>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Analitik Sekmesi */}
      {activeTab === 'analytics' && (
        <Card className="border-0 shadow-sm rounded-3">
          <Card.Body className="text-center py-5">
            <i className="bi bi-bar-chart-line-fill text-primary" style={{ fontSize: '48px' }}></i>
            <h4 className="mt-3">Analytics Dashboard</h4>
            <p className="text-muted">Analytics features will be available soon.</p>
          </Card.Body>
        </Card>
      )}

      {/* Yeni Aday Ekleme Modal */}
      <Modal show={showNewLeadModal} onHide={() => setShowNewLeadModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add New Lead</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="g-3">
              <Col xs={12} sm={6}>
                <Form.Group>
                  <Form.Label>Name</Form.Label>
                  <Form.Control type="text" />
                </Form.Group>
              </Col>
              <Col xs={12} sm={6}>
                <Form.Group>
                  <Form.Label>Company</Form.Label>
                  <Form.Control type="text" />
                </Form.Group>
              </Col>
              <Col xs={12} sm={6}>
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <Form.Control type="email" />
                </Form.Group>
              </Col>
              <Col xs={12} sm={6}>
                <Form.Group>
                  <Form.Label>Phone</Form.Label>
                  <Form.Control type="tel" />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Source</Form.Label>
                  <Form.Select defaultValue="website">
                    <option value="website">Website</option>
                    <option value="linkedin">LinkedIn</option>
                    <option value="email">Email Campaign</option>
                    <option value="referral">Referral</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNewLeadModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={() => setShowNewLeadModal(false)}
            style={{
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              border: 'none',
            }}
          >
            Add Lead
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Yeni Kampanya Oluşturma Modal */}
      <Modal show={showNewCampaignModal} onHide={() => setShowNewCampaignModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Create New Campaign</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="g-3">
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Campaign Name</Form.Label>
                  <Form.Control type="text" />
                </Form.Group>
              </Col>
              <Col xs={12} sm={6}>
                <Form.Group>
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control type="date" />
                </Form.Group>
              </Col>
              <Col xs={12} sm={6}>
                <Form.Group>
                  <Form.Label>End Date</Form.Label>
                  <Form.Control type="date" />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Target Industry</Form.Label>
                  <Form.Select defaultValue="tech">
                    <option value="tech">Technology</option>
                    <option value="finance">Finance</option>
                    <option value="healthcare">Healthcare</option>
                    <option value="retail">Retail</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNewCampaignModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={() => setShowNewCampaignModal(false)}
            style={{
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              border: 'none',
            }}
          >
            Create Campaign
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default LeadGenerationPage;
